import type { MelodyLib } from '@tunasong/music-lib'
import type { MelodyInstrumentHint } from '@tunasong/schemas'
import { Button } from '@tunasong/ui-lib'
import { useRef, useState } from 'react'
import type { FC } from 'react'
import { fitFreeTake, type FreeTempoFit } from './free-tempo-fit.js'
import styles from './free-tempo-tap.module.css'
import { MIN_TAPS } from './tap-downbeat.js'
import { useRawTakePlayback } from './use-raw-take-playback.js'

/**
 * §11.2 step 2: the take plays back and the user taps along with it, as they would count it.
 *
 * The first tap is the downbeat, because the user was asked to start on one - `tempoFromTaps`
 * states that rule and this panel is the surface for it. Taps are read off the TAKE's clock rather
 * than the wall clock, so stopping and playing again cannot drift the count.
 *
 * Nothing here decides a tempo. The taps only centre the search; `fitFreeTake` moves the grid onto
 * the notes (§11.2 step 3), which is why the panel's own readout never promises a number.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §11.2
 */

export interface FreeTempoTapProps {
  /** The retained raw take, in milliseconds. Never re-recorded by anything on this screen. */
  raw: MelodyLib.RawNote[]
  instrumentHint?: MelodyInstrumentHint
  onFit: (fit: FreeTempoFit) => void
  onRetake: () => void
  onCancel: () => void
  className?: string
}

export const FreeTempoTap: FC<FreeTempoTapProps> = props => {
  const { raw, instrumentHint, onFit, onRetake, onCancel, className } = props

  const playback = useRawTakePlayback(raw, instrumentHint)
  /** A ref, not state: a tap is recorded in the click's own tick and cannot wait for a render. */
  const taps = useRef<number[]>([])
  const [tapCount, setTapCount] = useState(0)

  /**
   * No `TAP_RESET_MS` here, unlike the other two tap surfaces.
   *
   * They restart the count after a pause because a pause is the only signal they have that the user
   * began again. Here the take stops playing when it ends and "Start the count again" is on screen,
   * so a gap between taps means the user was listening, not restarting - and silently discarding
   * their first four taps for it would be the worse guess.
   */
  const handleTap = () => {
    const at = playback.takeTimeMs()
    if (at === null) {
      /** Tapping against a stopped take would time the taps against nothing. */
      return
    }
    taps.current = [...taps.current, at]
    setTapCount(taps.current.length)
  }

  const handleClear = () => {
    taps.current = []
    setTapCount(0)
  }

  const handleFit = () => {
    const fit = fitFreeTake(raw, taps.current)
    if (!fit) {
      return
    }
    playback.stop()
    onFit(fit)
  }

  const remaining = MIN_TAPS - tapCount

  return (
    <div className={className ? `${styles.root} ${className}` : styles.root}>
      <span className={styles.instructions}>
        Play the take back and tap the beat you hear, starting on the downbeat. {MIN_TAPS} taps minimum — more is
        better.
      </span>

      <div className={styles.controls}>
        <Button onClick={playback.isPlaying ? playback.stop : playback.play}>
          {playback.isPlaying ? 'Stop' : 'Play the take'}
        </Button>
        <Button onClick={handleTap}>Tap</Button>
        <Button onClick={handleClear}>Start the count again</Button>
      </div>

      {/* Named by its own text, not by the instructions: the count changes as taps land. */}
      <span className={styles.readout} role="status">
        {remaining > 0 ? `${remaining} more tap${remaining === 1 ? '' : 's'}.` : `${tapCount} taps.`}
      </span>

      <div className={styles.actions}>
        <Button disabled={tapCount < MIN_TAPS} onClick={handleFit}>
          Find the beat
        </Button>
        <Button onClick={onRetake}>Record again</Button>
        <Button onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  )
}
